import React, { useCallback, useRef } from "react";

import UploadIcon from "@uui/Upload01";
import { isEmpty, noop } from "lodash/fp";

import { FormLabel, SxProps } from "@mui/material";
import Typography from "@mui/material/Typography";

import useDragging from "@rui/FileUpload/useDragging";
import { getInputAcceptAttr, validateFile } from "@rui/FileUpload/utils";
import IconBackground from "@rui/IconBackground";
import Link from "@rui/Link";
import { Column } from "@rui/Stack";

import DropContainer from "./DropContainer";
import { UploadPurchaseCta } from "./styles";
import type { FileCandidate } from "./types";

interface FileUploadProps {
  name: string;
  description?: string;
  disabled?: boolean;
  disabledCta?: {
    label: string;
    href: string;
    color?: "primary" | "secondary";
  };
  onChange: (files: FileCandidate[]) => void;
  types?: string[];
  sx?: SxProps;
  maxFileSizeMB?: number;
}

const FileUpload = ({
  name,
  description,
  disabled,
  disabledCta,
  maxFileSizeMB = 1024,
  onChange = noop,
  types = [],
  sx,
}: FileUploadProps) => {
  const id = `file-upload-${name}`;

  const inputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    handleDrop(event.target.files ?? new FileList());
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDrop = useCallback(
    (files: FileList) => {
      if (isEmpty(files) || disabled) return;

      const fileCandidates = Array.from(files).map((file) =>
        validateFile(file, { types, maxFileSizeMB })
      );
      onChange(fileCandidates);
    },
    [onChange, types, maxFileSizeMB, disabled]
  );

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    inputRef.current?.click();
  };

  const isDragging = useDragging({ labelRef, onDrop: handleDrop });

  return (
    <Column sx={{ width: "100%" }} variant="center">
      <input
        type="file"
        id={id}
        name={name}
        multiple
        onChange={handleChange}
        style={{ display: "none" }}
        ref={inputRef}
        accept={getInputAcceptAttr(types)}
        disabled={disabled}
      />
      <FormLabel
        htmlFor={id}
        sx={{ display: "flex", alignItems: "stretch", width: "100%", ...sx }}
        ref={labelRef}
      >
        <DropContainer
          hovering={isDragging}
          disabled={disabled}
          sx={{ width: "100%" }}
        >
          <Column variant="center" spacing={1}>
            {!disabled && (
              <IconBackground
                height={32}
                sx={{ backgroundColor: "shades.primary.50" }}
                padding={3}
              >
                <UploadIcon sx={{ color: "shades.primary.500" }} />
              </IconBackground>
            )}
            <Typography
              variant="bodyPrimaryMed"
              color={disabled ? "shades.neutral.500" : "shades.neutral.800"}
            >
              Drop a file here, or{" "}
              <Link
                underline={!disabled}
                sx={{ color: disabled ? "shades.neutral.500" : undefined }}
                onClick={disabled ? undefined : handleClick}
              >
                browse files
              </Link>
            </Typography>
            {description && (
              <Typography variant="bodySecondaryReg" color="shades.neutral.500">
                {description}
              </Typography>
            )}
          </Column>
          {disabled && disabledCta && (
            <UploadPurchaseCta
              color={disabledCta.color}
              href={disabledCta.href}
            >
              {disabledCta.label}
            </UploadPurchaseCta>
          )}
        </DropContainer>
      </FormLabel>
    </Column>
  );
};

export default FileUpload;
